import { observable, action, makeObservable } from 'mobx';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import dataModel from '../data';
import { updateQueryString } from '../../lib/helpers';
import LanguageDetector from './language-detector';

const supportedLanguages = ['ja', 'en'] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];
export const DEFAULT_LANGUAGE: SupportedLanguage = 'ja';

export class Locale {
  public language = '';
  public country = '';

  private countries: string[] = [];
  private i18next = i18next.use(initReactI18next);

  constructor() {
    makeObservable(this, {
      language: observable,
      country: observable,
      update: action
    });
  }

  public get supportedCountries() {
    return this.countries;
  }

  public async init() {
    this.setupI18next();

    await this.i18next.init({
      returnNull: false,
      interpolation: { escapeValue: false }, // not needed for react as it escapes by default
      fallbackLng: 'en' // Fallback language for missing keys. Some translations (e.g., 'defaultTitle') only exist in en.json
    });

    const { language: i18nextLangCountry } = this.i18next;
    // Can be in format "en" or "en-JP"
    const i18nextLanguage = i18nextLangCountry.split('-')[0];
    const language =
      (this.isSupportedLanguage(dataModel.locale) && dataModel.locale) ||
      (this.isSupportedLanguage(i18nextLanguage) && i18nextLanguage) ||
      DEFAULT_LANGUAGE;
    const country = dataModel.country || i18nextLangCountry.split('-')[1];

    await this.update(language, country);
  }

  public async update(
    language: SupportedLanguage,
    country: string
  ): Promise<void> {
    await this.i18next.changeLanguage(language);
    await this.loadLocalizations();

    this.language = this.i18next.language;

    if (this.countries.indexOf(country) !== -1) {
      this.country = country;
    }

    updateQueryString('locale', this.language);
  }

  private setupI18next() {
    this.countries = dataModel.supportedCountries;
    this.country = this.countries[0];

    const languageDetector = new LanguageDetector({
      supportedCountries: this.countries,
      defaultLanguage: DEFAULT_LANGUAGE,
      defaultCountry: this.country
    });
    this.i18next = i18next.use(languageDetector);
  }

  private loadLocalizations(): Promise<void[]> {
    return Promise.all(
      this.i18next.languages.reduce(
        (promises, bundleName: string) => {
          const filename = bundleName.replace('-', '_');

          if (!this.i18next.hasResourceBundle(bundleName, 'translation')) {
            promises.push(
              import(`../../localization/${filename}.json`).then((resource) => {
                this.i18next.addResourceBundle(
                  bundleName,
                  'translation',
                  resource
                );
              })
            );
          }

          return promises;
        },
        [] as Array<Promise<void>>
      )
    );
  }

  public isSupportedLanguage(lang: string): lang is SupportedLanguage {
    return supportedLanguages.includes(lang as SupportedLanguage);
  }
}

export default new Locale();
